import React, { useEffect, useState, useCallback } from 'react';
import { API, ax } from './adminHelpers';

const STATUS_COLORS = {
  pending: '#f5a623', approved: '#27C85A', contract_sent: '#9F5CFF',
  confirmed: '#27C85A', completed: '#06b6d4', declined: '#E34364',
};

function StatCard({ label, value, sub, color = '#E34364' }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: `1px solid ${color}22`,
      borderRadius: 16, padding: '20px 24px', flex: 1, minWidth: 160,
    }}>
      <p style={{ margin: '0 0 6px', fontSize: 12, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
      <p style={{ margin: '0 0 4px', fontSize: 28, color, fontWeight: 800 }}>{value}</p>
      {sub && <p style={{ margin: 0, fontSize: 11, color: '#555' }}>{sub}</p>}
    </div>
  );
}

function DynamicChart({ monthlyData, dailyData, selectedMonth, isDaily }) {
  if (isDaily) {
    if (!dailyData || dailyData.length === 0) {
      return (
        <div style={{ padding: '40px 0', textAlign: 'center', color: '#555', fontSize: 13 }}>
          No booking activity found for the month of {selectedMonth}.
        </div>
      );
    }

    // Generate all days in the selected month for a continuous continuous activity timeline
    const [year, month] = selectedMonth.split('-').map(Number);
    const numDays = new Date(year, month, 0).getDate();
    const fullDailyList = [];
    for (let d = 1; d <= numDays; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const match = dailyData.find(item => item.day === dateStr);
      fullDailyList.push({
        label: String(d),
        bookings: match ? parseInt(match.bookings) : 0,
        revenue: match ? parseFloat(match.revenue) : 0,
        date: dateStr,
      });
    }

    const maxRevenue = Math.max(...fullDailyList.map(d => d.revenue), 1);
    const maxBookings = Math.max(...fullDailyList.map(d => d.bookings), 1);

    return (
      <div style={{ overflowX: 'auto', paddingBottom: 8 }} className="no-scrollbar">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, minWidth: fullDailyList.length * 28, height: 200, padding: '0 4px' }}>
          {fullDailyList.map(d => {
            const revH = Math.round((d.revenue / maxRevenue) * 160);
            const bkgH = Math.round((d.bookings / maxBookings) * 160);
            return (
              <div key={d.date} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 20 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 170 }}>
                  <div title={`Date: ${d.date}\nRevenue: $${d.revenue.toFixed(2)}`} style={{
                    width: 8, height: revH || 2, background: 'linear-gradient(180deg,#E34364,#9F5CFF)',
                    borderRadius: '2px 2px 0 0', transition: 'height 0.3s',
                  }} />
                  <div title={`Date: ${d.date}\nBookings: ${d.bookings}`} style={{
                    width: 8, height: bkgH || 2, background: 'linear-gradient(180deg,#06b6d4,#27C85A)',
                    borderRadius: '2px 2px 0 0', transition: 'height 0.3s',
                  }} />
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 9, color: '#555', textAlign: 'center' }}>{d.label}</p>
                {d.revenue > 0 && (
                  <p style={{ margin: '2px 0 0', fontSize: 8, color: '#a0a0b8', textAlign: 'center' }}>${d.revenue >= 1000 ? (d.revenue / 1000).toFixed(0) + 'k' : d.revenue.toFixed(0)}</p>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 11, color: '#a0a0b8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg,#E34364,#9F5CFF)', display: 'inline-block' }} /> Revenue
            </span>
            <span style={{ fontSize: 11, color: '#a0a0b8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg,#06b6d4,#27C85A)', display: 'inline-block' }} /> Bookings
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>
            Showing daily activity breakdown for {selectedMonth}
          </span>
        </div>
      </div>
    );
  } else {
    if (!monthlyData || monthlyData.length === 0) {
      return <p style={{ color: '#555', fontSize: 13, padding: '40px 0', textAlign: 'center' }}>No booking activity found for the 12-month period.</p>;
    }

    const maxRevenue = Math.max(...monthlyData.map(d => parseFloat(d.revenue || 0)), 1);
    const maxBookings = Math.max(...monthlyData.map(d => parseInt(d.bookings || 0)), 1);

    return (
      <div style={{ overflowX: 'auto', paddingBottom: 8 }} className="no-scrollbar">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, minWidth: monthlyData.length * 72, height: 200, padding: '0 4px' }}>
          {monthlyData.map(d => {
            const rev = parseFloat(d.revenue || 0);
            const bkgs = parseInt(d.bookings || 0);
            const revH = Math.round((rev / maxRevenue) * 160);
            const bkgH = Math.round((bkgs / maxBookings) * 160);
            const label = d.month ? d.month.slice(5) + '/' + d.month.slice(2, 4) : '';
            return (
              <div key={d.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 56 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 170 }}>
                  <div title={`Month: ${d.month}\nRevenue: $${rev.toFixed(2)}`} style={{
                    width: 20, height: revH || 4, background: 'linear-gradient(180deg,#E34364,#9F5CFF)',
                    borderRadius: '4px 4px 0 0', transition: 'height 0.4s',
                  }} />
                  <div title={`Month: ${d.month}\nBookings: ${bkgs}`} style={{
                    width: 20, height: bkgH || 4, background: 'linear-gradient(180deg,#06b6d4,#27C85A)',
                    borderRadius: '4px 4px 0 0', transition: 'height 0.4s',
                  }} />
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 10, color: '#555', textAlign: 'center' }}>{label}</p>
                <p style={{ margin: '2px 0 0', fontSize: 10, color: '#a0a0b8', textAlign: 'center' }}>${rev >= 1000 ? (rev / 1000).toFixed(1) + 'k' : rev.toFixed(0)}</p>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 12, flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <span style={{ fontSize: 11, color: '#a0a0b8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg,#E34364,#9F5CFF)', display: 'inline-block' }} /> Revenue
            </span>
            <span style={{ fontSize: 11, color: '#a0a0b8', display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(180deg,#06b6d4,#27C85A)', display: 'inline-block' }} /> Bookings
            </span>
          </div>
          <span style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>
            Showing 12-month summary trend
          </span>
        </div>
      </div>
    );
  }
}

export default function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    return new Date().toISOString().slice(0, 7); // Default to current YYYY-MM
  });
  const [isDaily, setIsDaily] = useState(true);

  // Generate dynamic list of last 12 months for dropdown
  const generateMonths = () => {
    const list = [];
    const date = new Date();
    for (let i = 0; i < 12; i++) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const label = date.toLocaleString('default', { month: 'long', year: 'numeric' });
      list.push({ value: `${year}-${month}`, label });
      date.setMonth(date.getMonth() - 1);
    }
    return list;
  };

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (isDaily && selectedMonth) {
        params.month = selectedMonth;
      }
      
      const r = await ax().get(`${API}/api/admin/analytics`, { params });
      setData(r.data);
      setError('');
    } catch (err) {
      setError('Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedMonth, isDaily]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const handleReset = () => {
    setStartDate('');
    setEndDate('');
    const currentMonth = new Date().toISOString().slice(0, 7);
    setSelectedMonth(currentMonth);
    setIsDaily(true);
  };

  if (loading && !data) return <p style={{ color: '#a0a0b8', padding: 40, textAlign: 'center' }}>Loading analytics…</p>;
  if (error) return <p style={{ color: '#E34364', padding: 40 }}>{error}</p>;

  const totalBookings = data?.statusCounts?.reduce((s, r) => s + parseInt(r.count || 0), 0) || 0;
  const pendingCount = data?.statusCounts?.find(r => r.status === 'pending')?.count || 0;
  const completedCount = data?.statusCounts?.find(r => r.status === 'completed')?.count || 0;
  const confirmedCount = data?.statusCounts?.find(r => r.status === 'confirmed')?.count || 0;

  const section = (title, children) => (
    <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 24, marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 20px', fontSize: 15, color: '#fff', fontWeight: 700 }}>{title}</h3>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>📊 Analytics & Performance</h2>
        {loading && <span style={{ fontSize: 12, color: '#9F5CFF', animation: 'pulse 1.5s infinite' }}>Refreshing data…</span>}
      </div>

      {/* Filter Bar */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, padding: 20, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: 1, minWidth: 280, display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>End Date</label>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: '#fff', fontSize: 13, outline: 'none' }} />
          </div>
        </div>

        <div style={{ width: 220 }}>
          <label style={{ display: 'block', fontSize: 11, color: '#a0a0b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 6, letterSpacing: '0.05em' }}>Chart view mode</label>
          <select value={isDaily ? selectedMonth : 'trend'} onChange={e => {
            if (e.target.value === 'trend') {
              setIsDaily(false);
            } else {
              setIsDaily(true);
              setSelectedMonth(e.target.value);
            }
          }} style={{ width: '100%', padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, color: '#fff', fontSize: 13, cursor: 'pointer', outline: 'none' }}>
            <option value="trend" style={{ background: '#101428', color: '#fff' }}>📊 12-Month Trend</option>
            {generateMonths().map(m => (
              <option key={m.value} value={m.value} style={{ background: '#101428', color: '#fff' }}>📅 {m.label} (Daily)</option>
            ))}
          </select>
        </div>

        <button onClick={handleReset} style={{ padding: '11px 20px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#a0a0b8', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s', alignSelf: 'flex-end' }}>Reset</button>
      </div>

      {data && (
        <>
          {/* KPI Cards */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatCard label="Earnings" value={`$${data.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={startDate || endDate ? "Selected Date Range" : "All Confirmed + Completed"} color="#27C85A" />
            <StatCard label="Bookings" value={totalBookings} sub={startDate || endDate ? "Selected Date Range" : "All Time"} color="#9F5CFF" />
            <StatCard label="Pending Review" value={pendingCount} sub="Awaiting approval" color="#f5a623" />
            <StatCard label="Completed Events" value={completedCount} sub={`${confirmedCount} active confirmed`} color="#06b6d4" />
          </div>

          {/* Interactive Chart */}
          {section(
            isDaily ? `Daily Bookings & Earnings - ${generateMonths().find(m => m.value === selectedMonth)?.label || selectedMonth}` : 'Monthly Bookings & Revenue (Overall trend)',
            <DynamicChart monthlyData={data.monthly} dailyData={data.dailyBreakdown} selectedMonth={selectedMonth} isDaily={isDaily} />
          )}

          {/* Status Breakdown */}
          {section('Bookings by Status',
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.statusCounts.length === 0 ? (
                <p style={{ color: '#555', fontSize: 13 }}>No booking statuses recorded for the current filters.</p>
              ) : (
                data.statusCounts.map(r => {
                  const pct = totalBookings > 0 ? Math.round((parseInt(r.count) / totalBookings) * 100) : 0;
                  const color = STATUS_COLORS[r.status] || '#a0a0b8';
                  return (
                    <div key={r.status}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 13, color: '#e0e0e0', textTransform: 'capitalize' }}>{r.status.replace('_', ' ')}</span>
                        <span style={{ fontSize: 13, color: '#a0a0b8' }}>{r.count} ({pct}%)</span>
                      </div>
                      <div style={{ height: 6, background: 'rgba(255,255,255,0.05)', borderRadius: 6 }}>
                        <div style={{ height: 6, width: `${pct}%`, background: color, borderRadius: 6, transition: 'width 0.6s ease' }} />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Top Booths */}
          {section('Top Booths by Bookings',
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {data.topBooths.length === 0 ? (
                <p style={{ color: '#555', fontSize: 13 }}>No booth data recorded for the current filters.</p>
              ) : (
                data.topBooths.map((b, i) => (
                  <div key={b.booth_type} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    <span style={{ fontSize: 20, fontWeight: 800, color: i === 0 ? '#f5a623' : '#555', minWidth: 24, textAlign: 'center' }}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ margin: '0 0 2px', fontSize: 14, color: '#fff', fontWeight: 600 }}>{b.booth_type}</p>
                      <p style={{ margin: 0, fontSize: 11, color: '#a0a0b8' }}>{b.count} booking{b.count !== 1 ? 's' : ''}</p>
                    </div>
                    <span style={{ fontSize: 14, color: '#27C85A', fontWeight: 700 }}>${parseFloat(b.revenue || 0).toFixed(2)}</span>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Recent Bookings */}
          {section('Recent Bookings Activity',
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {data.recent.length === 0 ? (
                <p style={{ color: '#555', fontSize: 13 }}>No recent booking activity recorded.</p>
              ) : (
                data.recent.map(b => (
                  <div key={b.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <p style={{ margin: '0 0 2px', fontSize: 14, color: '#fff', fontWeight: 600 }}>{b.customer?.name}</p>
                      <p style={{ margin: 0, fontSize: 12, color: '#a0a0b8' }}>{b.event_date} · {b.booth_type}</p>
                    </div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: '#27C85A', fontWeight: 700 }}>${parseFloat(b.quoted_price || 0).toFixed(2)}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 50,
                        background: `${STATUS_COLORS[b.status] || '#555'}22`,
                        color: STATUS_COLORS[b.status] || '#a0a0b8',
                        border: `1px solid ${STATUS_COLORS[b.status] || '#555'}44`,
                      }}>{b.status.replace('_', ' ')}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
